import { type CacheResolver, createCacheResolver } from "../cache";
import type {
  CognitePort,
  ViewDefinition,
  ViewDefinitionProperty,
  ViewReference,
} from "../cognite";
import type { DataModelId } from "../types";
import {
  getDirectRelationSource,
  isEdgeConnection,
  isReverseDirectRelation,
  isViewPropertyDefinition,
} from "../utils/view";

export class ViewMapper {
  private readonly resolver: CacheResolver<Map<string, ViewDefinition>>;

  constructor(
    private readonly cognite: CognitePort,
    private readonly dataModelId: DataModelId,
  ) {
    this.resolver = createCacheResolver<Map<string, ViewDefinition>>({
      serialize: (views) => Array.from(views.values()),
      deserialize: (json) => {
        const views = json as ViewDefinition[];
        return new Map(views.map((view) => [view.externalId, view]));
      },
    });
  }

  async getView(externalId: string): Promise<ViewDefinition> {
    return requireView(await this.loadViews(), externalId, this.dataModelId);
  }

  async getViews(): Promise<ViewDefinition[]> {
    const views = await this.loadViews();
    return Array.from(views.values());
  }

  protected loadViews(): Promise<Map<string, ViewDefinition>> {
    return this.resolver.resolve(this.cacheKey(), () => this.fetchViews());
  }

  private cacheKey(): string {
    const { space, externalId, version } = this.dataModelId;
    return `view-mapper:${space}:${externalId}:${version}`;
  }

  private async fetchViews(): Promise<Map<string, ViewDefinition>> {
    const response = await this.cognite.retrieveDataModels(
      [
        {
          space: this.dataModelId.space,
          externalId: this.dataModelId.externalId,
          version: this.dataModelId.version,
        },
      ],
      { inlineViews: true },
    );

    const dm = response.items.sort((a, b) => b.createdTime - a.createdTime)[0];
    if (!dm) {
      throw new Error(`Data model "${this.dataModelId.externalId}" not found`);
    }

    const views = new Map<string, ViewDefinition>();
    for (const view of dm.views ?? []) {
      views.set(view.externalId, view);
    }

    await this.loadDependencyViews(views);
    return views;
  }

  private async loadDependencyViews(views: Map<string, ViewDefinition>): Promise<void> {
    const pending = new Map<string, ViewReference>();

    for (const view of views.values()) {
      for (const property of Object.values(view.properties)) {
        for (const ref of collectPropertyRefs(property)) {
          if (!views.has(ref.externalId) && !pending.has(ref.externalId)) {
            pending.set(ref.externalId, ref);
          }
        }
      }
    }

    if (pending.size === 0) return;

    const sizeBefore = views.size;
    const fetched = await this.cognite.retrieveViews(Array.from(pending.values()));
    for (const view of fetched.items) {
      views.set(view.externalId, view);
    }

    if (views.size > sizeBefore) {
      await this.loadDependencyViews(views);
    }
  }
}

/**
 * A `ViewMapper` preloaded with view definitions captured at package generation
 * time. `getView` / `getViews` never call CDF.
 */
export class ViewMapperCache extends ViewMapper {
  private readonly viewsByExternalId: Map<string, ViewDefinition>;

  constructor(views: Iterable<ViewDefinition> | Map<string, ViewDefinition>) {
    super(undefined as unknown as CognitePort, VIEW_MAPPER_CACHE_DATA_MODEL);
    this.viewsByExternalId = indexViews(views);
  }

  static fromViews(views: readonly object[]): ViewMapperCache {
    return new ViewMapperCache(views as ViewDefinition[]);
  }

  protected override loadViews(): Promise<Map<string, ViewDefinition>> {
    return Promise.resolve(this.viewsByExternalId);
  }
}

const VIEW_MAPPER_CACHE_DATA_MODEL: DataModelId = {
  space: "",
  externalId: "",
  version: "",
};

function indexViews(
  views: Iterable<ViewDefinition> | Map<string, ViewDefinition>,
): Map<string, ViewDefinition> {
  if (views instanceof Map) {
    return new Map(views);
  }
  return new Map(Array.from(views, (view) => [view.externalId, view]));
}

function requireView(
  views: Map<string, ViewDefinition>,
  externalId: string,
  dataModelId: DataModelId,
): ViewDefinition {
  const view = views.get(externalId);
  if (!view) {
    throw new Error(
      dataModelId.externalId
        ? `View "${externalId}" not found in data model "${dataModelId.externalId}"`
        : `View "${externalId}" is not available in data model`,
    );
  }
  return view;
}

function collectPropertyRefs(property: ViewDefinitionProperty): ViewReference[] {
  if (isViewPropertyDefinition(property)) {
    const source = getDirectRelationSource(property);
    return source ? [source] : [];
  }
  if (isReverseDirectRelation(property)) {
    return [property.source, property.through.source];
  }
  if (isEdgeConnection(property)) {
    return [property.source];
  }
  return [];
}
